/**
 * Batch-load latest beerProfileVersions for brewery beer list discoverability.
 * Approach A: chunked Firestore `in` queries + client reduce (Engage globalAdmin only).
 */

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { UserContext } from '../context/UserContext';
import { db } from '../utils/firebase';
import { BEER_PROFILE_VERSIONS_COLLECTION } from '../utils/firestoreCollections';
import {
  chunkBeerIdsForProfileVersionQuery,
  deriveBeerProfileShadowSummary,
  reduceLatestBeerProfileVersionsByBeerId,
} from '../utils/beer/beerProfileComparison';

/**
 * Stable key so identical id sets do not refetch when array identity changes.
 *
 * @param {string[] | null | undefined} beerIds
 * @returns {string}
 */
function beerIdsKey(beerIds) {
  if (!Array.isArray(beerIds) || beerIds.length === 0) {
    return '';
  }
  const unique = [];
  const seen = new Set();
  beerIds.forEach((beerId) => {
    if (typeof beerId !== 'string') {
      return;
    }
    const trimmed = beerId.trim();
    if (!trimmed || seen.has(trimmed)) {
      return;
    }
    seen.add(trimmed);
    unique.push(trimmed);
  });
  unique.sort();
  return unique.join('\0');
}

/**
 * @param {string[] | null | undefined} beerIds
 * @returns {{
 *   summariesByBeerId: Map<string, ReturnType<typeof deriveBeerProfileShadowSummary>>,
 *   loading: boolean,
 *   error: string | null,
 * }}
 */
export default function useBreweryBeerProfileShadowSummaries(beerIds) {
  const user = useContext(UserContext);
  const isGlobalAdmin = user?.isGlobalAdmin === true;
  const idsKey = useMemo(() => beerIdsKey(beerIds), [beerIds]);
  const [summariesByBeerId, setSummariesByBeerId] = useState(() => new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (!isGlobalAdmin || !idsKey) {
      setSummariesByBeerId(new Map());
      setLoading(false);
      setError(null);
      return undefined;
    }

    const chunks = chunkBeerIdsForProfileVersionQuery(idsKey.split('\0'));
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const snapshots = await Promise.all(
          chunks.map((chunk) => {
            const versionsQuery = query(
              collection(db, BEER_PROFILE_VERSIONS_COLLECTION),
              where('beerId', 'in', chunk),
            );
            return getDocs(versionsQuery);
          }),
        );
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }

        const versionDocs = [];
        snapshots.forEach((snapshot) => {
          snapshot.docs.forEach((docSnap) => {
            versionDocs.push({
              id: docSnap.id,
              ...docSnap.data(),
            });
          });
        });

        const latestByBeerId = reduceLatestBeerProfileVersionsByBeerId(versionDocs);
        const nextSummaries = new Map();
        latestByBeerId.forEach((version, beerId) => {
          nextSummaries.set(beerId, deriveBeerProfileShadowSummary(version));
        });
        setSummariesByBeerId(nextSummaries);
      } catch (loadError) {
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }
        console.error('Failed to load beerProfileVersions summaries', loadError);
        setSummariesByBeerId(new Map());
        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load shadow profile summaries',
        );
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isGlobalAdmin, idsKey]);

  return {
    summariesByBeerId,
    loading,
    error,
  };
}
